import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { HERO_SLIDES } from "../data/content";

const HeroCarousel = () => {
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);

  const goTo = (i) => setIndex((i + HERO_SLIDES.length) % HERO_SLIDES.length);
  const next = () => goTo(index + 1);
  const prev = () => goTo(index - 1);

  useEffect(() => {
    timerRef.current = setTimeout(next, 6500);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const slide = HERO_SLIDES[index];

  return (
    <section
      data-testid="hero-carousel"
      className="relative h-[92vh] min-h-[620px] w-full overflow-hidden bg-forest text-cream"
    >
      <AnimatePresence mode="sync">
        <motion.div
          key={slide.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.1, ease: "easeInOut" }}
          className="absolute inset-0"
        >
          <motion.div
            initial={{ scale: 1 }}
            animate={{ scale: 1.05 }}
            transition={{ duration: 20, ease: "linear" }}
            className="absolute inset-0 bg-center bg-cover"
            style={{ backgroundImage: `url(${slide.image})` }}
          />
          <div className="absolute bottom-0 left-0 right-0 h-[60%] bg-gradient-to-t from-black/60 to-transparent opacity-80" />
          <div className="grain" />
        </motion.div>
      </AnimatePresence>

      <div className="relative z-10 h-full container mx-auto px-6 md:px-10 xl:px-16 flex items-center pt-20">
        <div className="grid grid-cols-12 gap-6 w-full items-end">
          <div className="col-span-12 lg:col-span-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={slide.id}
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              >
                <p className="hidden md:block text-sm tracking-[0.2em] font-semibold text-white uppercase mb-6 drop-shadow-md" data-testid="hero-eyebrow">
                  {slide.eyebrow}
                </p>
                <h1 className="font-serif text-5xl md:text-6xl lg:text-8xl leading-[1.05] md:leading-[0.95] tracking-tight text-white drop-shadow-lg">
                  <span className="hidden md:block text-cream/90 text-2xl md:text-3xl font-sans font-light tracking-wide mb-4 drop-shadow">
                    Welcome to Rudrash Private Limited —
                  </span>
                  {slide.title}
                </h1>
                <p className="mt-6 md:mt-8 max-w-xl text-cream/95 text-lg md:text-xl font-medium leading-relaxed drop-shadow-md">
                  {slide.intro}
                </p>
                <div className="mt-10 flex flex-wrap items-center gap-4">
                  <Link
                    to={slide.ctaTo}
                    data-testid={`hero-cta-${slide.id}`}
                    className="btn-accent shadow-lg"
                  >
                    {slide.cta} <ArrowRight size={14} />
                  </Link>
                  <Link to="/contact" className="hidden sm:inline-flex btn-outline text-white border-white/50 hover:bg-white hover:text-black backdrop-blur-sm" data-testid="hero-cta-contact">
                    Partner With Us
                  </Link>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="hidden lg:flex col-span-4 justify-end items-end gap-3">
            <button
              onClick={prev}
              aria-label="Previous slide"
              data-testid="hero-prev"
              className="w-14 h-14 flex items-center justify-center border border-cream/40 hover:bg-cream hover:text-forest transition-colors"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={next}
              aria-label="Next slide"
              data-testid="hero-next"
              className="w-14 h-14 flex items-center justify-center border border-cream/40 hover:bg-cream hover:text-forest transition-colors"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* indicators */}
      <div className="absolute bottom-6 md:bottom-10 left-0 right-0 md:left-10 xl:left-16 z-20 flex items-center justify-center md:justify-start gap-4">
        {HERO_SLIDES.map((s, i) => (
          <button
            key={s.id}
            onClick={() => goTo(i)}
            data-testid={`hero-indicator-${s.id}`}
            className="group py-2 flex flex-col items-start gap-2"
            aria-label={`Go to slide ${i + 1}`}
          >
            <span
              className={`h-[2px] transition-all duration-500 rounded-full ${
                i === index ? "w-10 md:w-14 bg-saffron" : "w-6 md:w-8 bg-white/40 group-hover:bg-white/70"
              }`}
            />
          </button>
        ))}
      </div>
    </section>
  );
};

export default HeroCarousel;
